// src/services/api.js
import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: BASE });

// Attach JWT to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('ms_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('ms_token');
      window.location.href = '/';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const login      = (email) => api.post('/auth/login', { email });
export const verifyOtp  = (userId, code)    => api.post('/auth/verify-otp', { userId, code });
export const getMe      = ()                => api.get('/auth/me');
export const changePassword = (current_password, new_password) =>
  api.put('/auth/change-password', { current_password, new_password });
export const listUsers      = () => api.get('/auth/users');
export const resetPassword  = (userId, new_password) =>
  api.put(`/auth/users/${userId}/reset-password`, { new_password });

// ── Products ──────────────────────────────────────────────────
export const getProducts = () => api.get('/products');

// ── Orders ───────────────────────────────────────────────────
export const getOrders      = (status) => api.get('/orders', { params: status ? { status } : {} });
export const getOrderById   = (id)     => api.get(`/orders/${id}`);
export const createOrder    = (data)   => api.post('/orders', data);
export const updateStatus   = (id, status) => api.patch(`/orders/${id}/status`, { status });

// ── Invoices ─────────────────────────────────────────────────
export const getInvoices    = ()        => api.get('/invoices');
export const getInvoiceById = (id)      => api.get(`/invoices/${id}`);
export const sendInvoice    = (id)      => api.patch(`/invoices/${id}/send`);
export const recordPayment  = (id, data)=> api.post(`/invoices/${id}/payments`, data);

// PDF downloads go through the axios instance so the JWT (which lives in
// localStorage, not a cookie) is attached — a plain <a href> would get 401.
export const downloadInvoicePDF  = (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
export const downloadReceiptPDF  = (orderId) => api.get(`/receipts/${orderId}/pdf`, { responseType: 'blob' });

// Triggers a browser download for a blob returned by the PDF endpoints.
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Reports ───────────────────────────────────────────────────
export const getSummary      = () => api.get('/reports/summary');
export const getPipeline     = () => api.get('/reports/pipeline');
export const getFinanceReport= () => api.get('/reports/finance');
export const settleProfitPool  = (notes) => api.post('/reports/finance/settle', { notes });
export const getSettlements    = () => api.get('/reports/finance/settlements');
export const getSettlementById = (id) => api.get(`/reports/finance/settlements/${id}`);
export const getAuditLogs    = () => api.get('/audit');

// ── Settings ──────────────────────────────────────────────────
export const getFinanceSettings    = () => api.get('/settings/finance');
export const updateFinanceSettings = (business_fund_percent, profit_pool_members) =>
  api.put('/settings/finance', { business_fund_percent, profit_pool_members });

// ── Products / Price list ─────────────────────────────────────
export const listProducts   = () => api.get('/products');
export const createProduct  = (data) => api.post('/products', data);
export const updateProduct  = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct  = (id) => api.delete(`/products/${id}`);

// ── Expenses ──────────────────────────────────────────────────
export const listExpenses   = () => api.get('/expenses');
export const createExpense  = (data) => api.post('/expenses', data);
export const deleteExpense  = (id) => api.delete(`/expenses/${id}`);

// ── Printing cost ────────────────────────────────────────────
export const setPrintingCost = (orderId, printing_cost) =>
  api.patch(`/orders/${orderId}/printing-cost`, { printing_cost });
export const listPermissions = () => api.get('/permissions');
export const listRoles       = () => api.get('/roles');
export const createRole      = (data) => api.post('/roles', data);
export const updateRole      = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole      = (id) => api.delete(`/roles/${id}`);

// ── Staff accounts (management only) ────────────────────────────
export const createUser   = (data) => api.post('/users', data);
export const setUserRole  = (id, role) => api.put(`/users/${id}/role`, { role });
export const setUserStatus= (id, is_active) => api.put(`/users/${id}/status`, { is_active });

// ── Chat ──────────────────────────────────────────────────────
export const listConversations = () => api.get('/chat/conversations');
export const getMessages       = (id, after) => api.get(`/chat/conversations/${id}/messages`, { params: after ? { after } : {} });
export const sendChatMessage   = (id, body) => api.post(`/chat/conversations/${id}/messages`, { body });
export const markConversationRead = (id) => api.post(`/chat/conversations/${id}/read`);
export const getOrCreateDM     = (userId) => api.post(`/chat/dm/${userId}`);

// ── Public website (no auth) ────────────────────────────────────
export const getPublicBooks = () => api.get('/public/books');
export const trackPageView  = (page = 'home') => api.post('/public/view', { page });

// ── Website management — Authors & Books CMS + view analytics ──
export const listWebsiteBooks  = () => api.get('/website/books');
export const createWebsiteBook = (data) => api.post('/website/books', data);
export const updateWebsiteBook = (id, data) => api.put(`/website/books/${id}`, data);
export const deleteWebsiteBook = (id) => api.delete(`/website/books/${id}`);
export const getWebsiteStats   = () => api.get('/website/stats');

export default api;
